<script>
	import { t } from '$lib/i18n.svelte';
	import { onMount, onDestroy } from 'svelte';
	import { fly } from 'svelte/transition';
	import { browser } from '$app/environment';

	/**
	 * @typedef {Object} SmallMenuItem
	 * @property {string} id
	 * @property {string} label
	 * @property {() => void} [action]
	 * @property {string} [icon]
	 * @property {Boolean} [boolValue]
	 */

	/** @type {{
	 *   items: SmallMenuItem[],
	 *   displayMenu: boolean,
	 *   position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
	 * }} */
	let {
		items = $bindable([]),
		displayMenu = $bindable(false),
		position = $bindable('bottom-right')
	} = $props();

	let menuRef = $state();

	/** @param {MouseEvent} e */
	function handleClickOutside(e) {
		if (menuRef && !menuRef.contains(e.target)) displayMenu = false;
	}

	/** @param {KeyboardEvent} e */
	function onKeyDown(e) {
		if (e.key === 'Escape') displayMenu = false;
	}

	onMount(() => {
		if (browser) {
			document?.addEventListener('click', handleClickOutside);
			document?.addEventListener('keydown', onKeyDown);
		}
	});
	onDestroy(() => {
		if (browser) {
			document?.removeEventListener('click', handleClickOutside);
			document?.removeEventListener('keydown', onKeyDown);
		}
	});

	/**@param {SmallMenuItem} item */
	function handleItemClick(item) {
		if (item.id && item.id.startsWith('bool_')) {
			item.boolValue = !item.boolValue;
		} else if (item.action) {
			item.action();
			displayMenu = false;
		}
	}

	let positionClass = $derived(
		{
			'top-left': 'right-0 bottom-full mb-2',
			'top-right': 'left-0 bottom-full mb-2',
			'bottom-left': 'right-8 mt-2',
			'bottom-right': 'left-8 mt-2'
		}[position]
	);
</script>

{#if displayMenu}
	<div
		bind:this={menuRef}
		in:fly={{ y: 20, duration: 200 }}
		out:fly={{ y: 20, duration: 100 }}
		class="absolute bg-white dark:bg-ord-cardcolor-dark rounded-md shadow-lg border border-grey-stroke-signature dark:border-ord-border-dark z-50 overflow-hidden w-56 {positionClass}"
	>
		<!-- Boolean items first -->
		{#each items.filter((item) => item.id?.startsWith('bool_')) as item, i (item.id)}
			<button
				class="w-full text-left py-1.5 px-3 hover:bg-ord-grey dark:hover:bg-ord-darkgrey dark:text-white flex items-center gap-3"
				onclick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleItemClick(item);
				}}
			>
				<div title={item.label} class="line-clamp-1">
					<input
						type="checkbox"
						bind:checked={item.boolValue}
						class="mr-2 translate-y-1 checkbox"
					/>
					{item.label}
				</div>
			</button>
		{/each}

		<!-- Separator (only shown if there are both boolean and non-boolean items) -->
		{#if items.some( (item) => item.id?.startsWith('bool_') ) && items.some((item) => !item.id?.startsWith('bool_'))}
			<div
				class="border-b border-dashed border-grey-stroke-signature dark:border-ord-border-dark mx-2"
			></div>
		{/if}

		<!-- Non-boolean items -->
		{#each items.filter((item) => !item.id?.startsWith('bool_')) as item}
			<button
				class="w-full text-left py-1.5 px-3 hover:bg-ord-grey dark:hover:bg-ord-darkgrey dark:text-white flex items-center gap-3 font-medium"
				onclick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleItemClick(item);
				}}
			>
				{#if item.icon}
					<img src={item.icon} alt={item.label} class="w-5 flex-none dark:invert" />
				{/if}
				<div title={item.label} class="line-clamp-1">
					{item.label}
				</div>
			</button>
		{/each}
	</div>
{/if}
